import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { getActiveRemixCreateId } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';

export const POTENTIAL_REMIX_TIMEOUT_MS = 120_000;

export type RemixGenerationAnalyticsAttributes = {
	contentId?: string;
	conversationChannelId?: string;
	entryPoint: string;
	featureArea?: string;
	includesFreegenPrompt: boolean;
	product: string;
	remixSource?: string;
	remixSubtype?: string;
	remixType: string;
};

type ActiveRemixGenerationAttributes = RemixGenerationAnalyticsAttributes & {
	remixCreateId?: string;
};

type RemixGenerationAnalytics = {
	trackGenerationFailed: (attributes?: Partial<RemixGenerationAnalyticsAttributes>) => void;
	trackGenerationStarted: (attributes: RemixGenerationAnalyticsAttributes) => void;
	trackGenerationStopped: (attributes?: Partial<RemixGenerationAnalyticsAttributes>) => void;
	trackGenerationSucceeded: (attributes?: Partial<RemixGenerationAnalyticsAttributes>) => void;
	trackRemixSubmitted: (attributes: RemixGenerationAnalyticsAttributes) => void;
};

export const useRemixGenerationAnalytics = (): RemixGenerationAnalytics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const activeGenerationRef = useRef<ActiveRemixGenerationAttributes | undefined>(undefined);
	const hasRecordedSuccessRef = useRef(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const clearPotentialTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	const createAttributes = useCallback(
		(attributes: RemixGenerationAnalyticsAttributes): ActiveRemixGenerationAttributes => ({
			...attributes,
			featureArea:
				attributes.featureArea ??
				getRemixFeatureArea({
					remixType: attributes.remixType,
					existingFeatureArea: 'remix',
				}) ??
				'remix',
			remixCreateId: getActiveRemixCreateId(),
		}),
		[],
	);

	const trackRemixSubmitted = useCallback(
		(attributes: RemixGenerationAnalyticsAttributes) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'remixSubmit',
					source: attributes.remixSource ?? attributes.entryPoint,
					attributes: createAttributes(attributes),
				},
			}).fire();
		},
		[createAnalyticsEvent, createAttributes],
	);

	const trackGenerationStarted = useCallback(
		(attributes: RemixGenerationAnalyticsAttributes) => {
			clearPotentialTimeout();
			const activeAttributes = createAttributes(attributes);
			activeGenerationRef.current = activeAttributes;
			hasRecordedSuccessRef.current = false;
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'started',
					actionSubject: 'remixGeneration',
					source: attributes.remixSource ?? attributes.entryPoint,
					attributes: activeAttributes,
				},
			}).fire();
			timeoutRef.current = setTimeout(() => {
				const currentAttributes = activeGenerationRef.current;
				if (!currentAttributes) {
					return;
				}
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'potentialTimeout',
						actionSubject: 'remixGeneration',
						source: currentAttributes.remixSource ?? currentAttributes.entryPoint,
						attributes: currentAttributes,
					},
				}).fire();
			}, POTENTIAL_REMIX_TIMEOUT_MS);
		},
		[clearPotentialTimeout, createAnalyticsEvent, createAttributes],
	);

	const trackGenerationFinished = useCallback(
		(action: 'failed' | 'succeeded', attributes?: Partial<RemixGenerationAnalyticsAttributes>) => {
			const activeAttributes = activeGenerationRef.current;
			if (!activeAttributes) {
				return;
			}
			if (action === 'succeeded' && hasRecordedSuccessRef.current) {
				return;
			}
			clearPotentialTimeout();
			const finalAttributes = { ...activeAttributes, ...attributes };
			if (action === 'failed') {
				activeGenerationRef.current = undefined;
			} else {
				hasRecordedSuccessRef.current = true;
				activeGenerationRef.current = undefined;
			}
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action,
					actionSubject: 'remixGeneration',
					source: finalAttributes.remixSource ?? finalAttributes.entryPoint,
					attributes: finalAttributes,
				},
			}).fire();
		},
		[clearPotentialTimeout, createAnalyticsEvent],
	);

	const trackGenerationStopped = useCallback(
		(attributes?: Partial<RemixGenerationAnalyticsAttributes>) => {
			const activeAttributes = activeGenerationRef.current;
			if (!activeAttributes) {
				return;
			}
			clearPotentialTimeout();
			activeGenerationRef.current = undefined;
			hasRecordedSuccessRef.current = false;
			const finalAttributes = { ...activeAttributes, ...attributes };
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'stopped',
					actionSubject: 'remixGeneration',
					source: finalAttributes.remixSource ?? finalAttributes.entryPoint,
					attributes: finalAttributes,
				},
			}).fire();
		},
		[clearPotentialTimeout, createAnalyticsEvent],
	);

	const trackGenerationFailed = useCallback(
		(attributes?: Partial<RemixGenerationAnalyticsAttributes>) =>
			trackGenerationFinished('failed', attributes),
		[trackGenerationFinished],
	);
	const trackGenerationSucceeded = useCallback(
		(attributes?: Partial<RemixGenerationAnalyticsAttributes>) =>
			trackGenerationFinished('succeeded', attributes),
		[trackGenerationFinished],
	);

	useEffect(
		() => () => {
			clearPotentialTimeout();
			activeGenerationRef.current = undefined;
			hasRecordedSuccessRef.current = false;
		},
		[clearPotentialTimeout],
	);

	return {
		trackGenerationFailed,
		trackGenerationStarted,
		trackGenerationStopped,
		trackGenerationSucceeded,
		trackRemixSubmitted,
	};
};
